import { useLocalStorage } from '@mantine/hooks'
import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getContentSafetyScores,
  getGenerationModelArtworkSafetyScore,
} from 'src/utils/contentSafety'
import { getI18nKeyForI2vModelName } from 'src/utils/task'

interface InfoCell {
  label: string
  value: string
  title: string
  color: string
}

export function CardCornerDebugInfo(props: {
  extra?: {
    textSafetyScore?: number
    mediaSafetyScore?: number
    i2vProParameters?: Record<string, any>
  }
  // oRPC list endpoints surface safety scores via a dedicated `debugInfo`
  // payload instead of embedding them in the GraphQL JSONB `extra`. Either
  // source is acceptable — the component picks the first non-null value.
  debugInfo?: {
    textSafetyScore?: number | null
    mediaSafetyScore?: number | null
  } | null
  feedMetadata?: {
    retrieverId?: string
  }
  artworkSafetyScore?: {
    safetyScoreCount?: number
    safetyScoreSum?: number
  }
  paidCredit?: number
  aesScore?: number
  children: (content: ReactNode) => ReactNode
}) {
  const { t } = useTranslation(['generation'])
  const [showGeneratedContentDebugInfo] = useLocalStorage({
    key: 'debug::show-generated-content-debug-info',
    defaultValue: false,
  })
  const textSafetyScore =
    props.extra?.textSafetyScore ?? props.debugInfo?.textSafetyScore ?? null
  const mediaSafetyScore =
    props.extra?.mediaSafetyScore ?? props.debugInfo?.mediaSafetyScore ?? null
  const hasSafetyScore = textSafetyScore != null || mediaSafetyScore != null
  const hasArtworkSafetyScore =
    props.artworkSafetyScore?.safetyScoreCount != null &&
    props.artworkSafetyScore?.safetyScoreSum != null

  const firstRow: InfoCell[] = []
  if (hasSafetyScore) {
    const scores = getContentSafetyScores({
      textSafetyScore: textSafetyScore ?? undefined,
      mediaSafetyScore: mediaSafetyScore ?? undefined,
    })
    firstRow.push({
      label: 'T',
      value: scores.textSafetyScore,
      title: 'Text Safety Score',
      color: 'text-blue-400',
    })
    firstRow.push({
      label: 'M',
      value: scores.mediaSafetyScore,
      title: 'Media Safety Score',
      color: 'text-green-400',
    })
  }
  if (hasArtworkSafetyScore) {
    firstRow.push({
      label: 'AM',
      value: getGenerationModelArtworkSafetyScore(props.artworkSafetyScore),
      title: 'Artwork Media Safety Score',
      color: 'text-purple-400',
    })
  }
  if (props.aesScore != null) {
    firstRow.push({
      label: 'A',
      value: props.aesScore.toFixed(1),
      title: 'Aesthetic Score',
      color: 'text-yellow-400',
    })
  }

  const secondRow: InfoCell[] = []
  if (props.paidCredit != null) {
    secondRow.push({
      label: 'C',
      value: props.paidCredit.toString(),
      title: 'cost',
      color: 'text-blue-400',
    })
  }
  const i2vProModel = props.extra?.i2vProParameters?.model
  if (i2vProModel != null) {
    secondRow.push({
      label: '',
      value: i2vProModel,
      title: t([
        `generation:image-to-video.parameters.model.${getI18nKeyForI2vModelName(i2vProModel)}.label`,
        'common:unknown',
      ]),
      color: 'text-green-400',
    })
  }

  const thirdRow: InfoCell[] = []
  if (props.feedMetadata?.retrieverId != null) {
    thirdRow.push({
      label: '',
      value: props.feedMetadata.retrieverId,
      title: 'retriever id',
      color: 'text-white/70',
    })
  }

  if (!showGeneratedContentDebugInfo) return null
  const rows = [firstRow, secondRow, thirdRow]
  if (rows.every(row => row.length === 0)) return null

  const content = (
    <>
      {rows.map(
        cells =>
          cells.length > 0 && (
            <p className="flex items-center justify-end gap-1">
              {cells.map(({ label, value, title, color }, index) => (
                <Fragment key={`${label}-${value}-${index}`}>
                  <span className={`${color} text-sm`} title={title}>
                    {label}
                    <span className="font-semibold">{value}</span>
                  </span>
                  {index < cells.length - 1 && (
                    <span className="text-white/70">/</span>
                  )}
                </Fragment>
              ))}
            </p>
          ),
      )}
    </>
  )
  return <>{props.children(content)}</>
}
